import React from 'react'
import { Download, Printer, X } from 'lucide-react'

function InvoiceGenerator({ workEntry, customer, vehicle, onClose }) {
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0)
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN')
  }

  // Calculate the base amount from work items (this is the amount before discount, including betta amounts)
  const baseAmountFromWorkItems = workEntry.workItems && workEntry.workItems.length > 0 
    ? workEntry.workItems.reduce((sum, item) => sum + parseFloat(item.amount || 0) + parseFloat(item.betaAmount || 0), 0)
    : parseFloat(workEntry.amount || 0)
  
  // Calculate discount amount safely
  const discountAmount = workEntry.discountAmount && workEntry.discountAmount !== '' 
    ? parseFloat(workEntry.discountAmount) 
    : 0
  
  // Calculate total amount using the correct base amount and applying discount
  const baseAmountAfterDiscount = baseAmountFromWorkItems - discountAmount
  const totalAmount = baseAmountAfterDiscount + parseFloat(workEntry.taxAmount || 0)

  const handlePrint = () => {
    // Add print-specific styles
    const printStyles = document.createElement('style')
         printStyles.innerHTML = `
       @media print {
         @page { margin: 0.25in; size: A4; }
         body { margin: 0; padding: 0; background: white !important; }
         * { box-sizing: border-box; }
         
         /* Ensure the invoice container is the main content */
         .invoice-container {
           position: relative !important;
           z-index: 9999 !important;
         }
        
        /* Reduce spacing for compact layout */
        .print\\:space-y-4 > * + * { margin-top: 0.5rem !important; }
        .print\\:p-4 { padding: 0.5rem !important; }
        
        /* Prevent page breaks */
        table { page-break-inside: avoid !important; }
        .bg-gray-50 { background-color: #f9fafb !important; }
        .border { border: 1px solid #d1d5db !important; }
        .border-gray-300 { border-color: #d1d5db !important; }
        
        /* Text alignment */
        .text-center { text-align: center !important; }
        .text-right { text-align: right !important; }
        
        /* Font weights */
        .font-bold { font-weight: bold !important; }
        .font-semibold { font-weight: 600 !important; }
        
        /* Font sizes for print - ensuring headings are visible */
        .text-3xl { font-size: 1.25rem !important; line-height: 1.1 !important; font-weight: bold !important; }
        .text-2xl { font-size: 1rem !important; line-height: 1.2 !important; font-weight: bold !important; }
        .text-lg { font-size: 0.875rem !important; line-height: 1.3 !important; }
        .text-sm { font-size: 0.625rem !important; line-height: 1.2 !important; }
        
        /* Reduced padding */
        .p-4 { padding: 0.25rem !important; }
        .px-4 { padding-left: 0.25rem !important; padding-right: 0.25rem !important; }
        .py-3 { padding-top: 0.125rem !important; padding-bottom: 0.125rem !important; }
        .mb-2 { margin-bottom: 0.125rem !important; }
        .mb-1 { margin-bottom: 0.0625rem !important; }
        .pb-4 { padding-bottom: 0.25rem !important; }
        
        /* Reduced spacing */
        .space-y-6 > * + * { margin-top: 0.25rem !important; }
        .space-y-2 > * + * { margin-top: 0.0625rem !important; }
        .gap-6 { gap: 0.25rem !important; }
        
        /* Layout */
        .grid { display: grid !important; }
        .grid-cols-1 { grid-template-columns: repeat(1, minmax(0, 1fr)) !important; }
        .md\\:grid-cols-2 { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; }
        .overflow-x-auto { overflow-x: visible !important; }
        .rounded-lg { border-radius: 0.25rem !important; }
        
        /* Borders */
        .border-b-2 { border-bottom-width: 2px !important; }
        .border-b { border-bottom-width: 1px !important; }
        .border-t-2 { border-top-width: 2px !important; }
        
        /* Width and positioning */
        .w-full { width: 100% !important; }
        .w-32 { width: 6rem !important; }
        .mx-auto { margin-left: auto !important; margin-right: auto !important; }
        
        /* Flexbox */
        .flex { display: flex !important; }
        .justify-between { justify-content: space-between !important; }
        .justify-end { justify-content: flex-end !important; }
        .items-start { align-items: flex-start !important; }
        .items-center { align-items: center !important; }
        .space-x-3 > * + * { margin-left: 0.5rem !important; }
        .space-x-2 > * + * { margin-left: 0.25rem !important; }
        
        /* Icons */
        .h-4 { height: 0.75rem !important; }
        .w-4 { width: 0.75rem !important; }
        
        /* Colors */
        .text-gray-900 { color: #111827 !important; }
        .text-gray-700 { color: #374151 !important; }
        .text-gray-600 { color: #4b5563 !important; }
        .text-gray-50 { background-color: #f9fafb !important; }
        .bg-white { background-color: white !important; }
        .border-gray-200 { border-color: #e5e7eb !important; }
        .border-gray-300 { border-color: #d1d5db !important; }
        .border-gray-400 { border-color: #9ca3af !important; }
        
        /* Hide buttons and other elements */
        .btn { display: none !important; }
        .flex.justify-end.space-x-3 { display: none !important; }
        
        /* Hide any page titles or headers that might contain "Generate Invoice" */
        h1:not(.text-3xl), h2:not(.text-2xl), h3:not(.text-lg) { display: none !important; }
        .page-title, .header-title, .modal-title { display: none !important; }
        
        /* Ensure invoice container is visible and properly sized */
        .invoice-container { 
          display: block !important; 
          width: 100% !important;
          max-width: none !important;
          margin: 0 !important;
          padding: 0.5rem !important;
        }
        
        /* Force single page */
        .invoice-container { page-break-after: avoid !important; page-break-before: avoid !important; }
        
        /* Ensure company header is visible */
        .text-center.border-b-2 { 
          border-bottom: 2px solid #d1d5db !important; 
          padding-bottom: 0.5rem !important; 
          margin-bottom: 0.5rem !important; 
        }
        
        /* Ensure all headings are visible */
        h1, h2, h3, h4 { 
          color: #111827 !important; 
          font-weight: bold !important; 
          display: block !important; 
        }
      }
    `
    document.head.appendChild(printStyles)
    
    // Print the page
    window.print()
    
    // Remove the print styles after printing
    setTimeout(() => {
      document.head.removeChild(printStyles)
    }, 1000)
  }

  const handleDownload = () => {
    // Create a new window with the invoice content
    const printWindow = window.open('', '_blank')
    
    // Get the invoice content
    const invoiceContent = document.querySelector('.invoice-container').innerHTML
    
    // Create the HTML document for PDF
    const htmlContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Invoice - ${workEntry.invoiceNumber || `2025-26/${workEntry.id.slice(0, 2).toUpperCase()}`}</title>
          <style>
            @page { margin: 0.25in; size: A4; }
            body { 
              margin: 0; 
              padding: 0; 
              background: white; 
              font-family: Arial, sans-serif;
              font-size: 12px;
            }
            * { box-sizing: border-box; }
            
            .invoice-container { 
              width: 100%;
              max-width: none;
              margin: 0;
              padding: 0.5rem;
            }
            
            .text-3xl { font-size: 1.25rem; line-height: 1.1; font-weight: bold; }
            .text-2xl { font-size: 1rem; line-height: 1.2; font-weight: bold; }
            .text-lg { font-size: 0.875rem; line-height: 1.3; }
            .text-sm { font-size: 0.625rem; line-height: 1.2; }
            
            .p-4 { padding: 0.25rem; }
            .px-4 { padding-left: 0.25rem; padding-right: 0.25rem; }
            .py-3 { padding-top: 0.125rem; padding-bottom: 0.125rem; }
            .mb-2 { margin-bottom: 0.125rem; }
            .mb-1 { margin-bottom: 0.0625rem; }
            .pb-4 { padding-bottom: 0.25rem; }
            
            .space-y-6 > * + * { margin-top: 0.25rem; }
            .space-y-2 > * + * { margin-top: 0.0625rem; }
            .gap-6 { gap: 0.25rem; }
            
            /* Ensure proper spacing between tables in PDF */
            .mt-6 { margin-top: 1.5rem !important; }
            
            .grid { display: grid; }
            .grid-cols-1 { grid-template-columns: repeat(1, minmax(0, 1fr)); }
            .md\\:grid-cols-2 { grid-template-columns: repeat(2, minmax(0, 1fr)); }
            
            .text-center { text-align: center; }
            .text-right { text-align: right; }
            .text-left { text-align: left; }
            
            .font-bold { font-weight: bold; }
            .font-semibold { font-weight: 600; }
            
            .w-full { width: 100%; }
            .w-32 { width: 6rem; }
            .mx-auto { margin-left: auto; margin-right: auto; }
            
            .flex { display: flex; }
            .justify-between { justify-content: space-between; }
            .justify-end { justify-content: flex-end; }
            .items-start { align-items: flex-start; }
            .items-center { align-items: center; }
            
            .bg-gray-50 { background-color: #f9fafb; }
            .bg-white { background-color: white; }
            .bg-gray-50 { background-color: #f9fafb; }
            
            .border { border: 1px solid #d1d5db; }
            .border-b-2 { border-bottom-width: 2px; }
            .border-b { border-bottom-width: 1px; }
            .border-t-2 { border-top-width: 2px; }
            .border-gray-200 { border-color: #e5e7eb; }
            .border-gray-300 { border-color: #d1d5db; }
            .border-gray-400 { border-color: #9ca3af; }
            
            .text-gray-900 { color: #111827; }
            .text-gray-700 { color: #374151; }
            .text-gray-600 { color: #4b5563; }
            
            .rounded-lg { border-radius: 0.25rem; }
            .overflow-hidden { overflow: hidden; }
            
            table { width: 100%; border-collapse: collapse; }
            th, td { border: 1px solid #d1d5db; padding: 0.125rem; }
            
            .text-center.border-b-2 { 
              border-bottom: 2px solid #d1d5db; 
              padding-bottom: 0.5rem; 
              margin-bottom: 0.5rem; 
            }
            
            h1, h2, h3, h4 { 
              color: #111827; 
              font-weight: bold; 
              display: block; 
            }
            
            /* Ensure invoice title is visible in PDF */
            .invoice-title h2 { 
              display: block !important; 
              color: #111827 !important; 
              font-weight: bold !important; 
              font-size: 1.5rem !important;
              text-align: center !important;
              margin-bottom: 1rem !important;
            }
            
                         img { 
               max-width: 100%; 
               height: auto; 
               max-height: 60px !important;
               width: auto !important;
             }
             
             /* Specific styling for signature image */
             .w-48.h-24 {
               width: 120px !important;
               height: 60px !important;
               max-height: 60px !important;
             }
          </style>
        </head>
        <body>
          <div class="invoice-container">
            ${invoiceContent}
          </div>
        </body>
      </html>
    `
    
    printWindow.document.write(htmlContent)
    printWindow.document.close()
    
    // Wait for content to load, then print
    printWindow.onload = () => {
      printWindow.print()
      printWindow.close()
    }
  }



  return (
    <div className="space-y-6 print:space-y-2 print:p-2 invoice-container">
      {/* Print Styles */}
             <style jsx>{`
         @media print {
           @page { margin: 0.25in; size: A4; }
           body { margin: 0; padding: 0; background: white !important; }
           * { box-sizing: border-box; }
           
           /* Ensure the invoice container is the main content */
           .invoice-container {
             position: relative !important;
             z-index: 9999 !important;
           }
          
          /* Reduce spacing for compact layout */
          .print\\:space-y-2 > * + * { margin-top: 0.5rem !important; }
          .print\\:p-2 { padding: 0.5rem !important; }
          
          /* Prevent page breaks */
          table { page-break-inside: avoid !important; }
          .bg-gray-50 { background-color: #f9fafb !important; }
          .border { border: 1px solid #d1d5db !important; }
          .border-gray-300 { border-color: #d1d5db !important; }
          
          /* Text alignment */
          .text-center { text-align: center !important; }
          .text-right { text-align: right !important; }
          
          /* Font weights */
          .font-bold { font-weight: bold !important; }
          .font-semibold { font-weight: 600 !important; }
          
                     /* Font sizes for print - ensuring headings are visible */
           .text-3xl { font-size: 1.25rem !important; line-height: 1.1 !important; font-weight: bold !important; }
           .text-2xl { font-size: 1rem !important; line-height: 1.2 !important; font-weight: bold !important; }
           .text-lg { font-size: 0.875rem !important; line-height: 1.3 !important; }
           .text-sm { font-size: 0.625rem !important; line-height: 1.2 !important; }
          
          /* Reduced padding */
          .p-4 { padding: 0.25rem !important; }
          .px-4 { padding-left: 0.25rem !important; padding-right: 0.25rem !important; }
          .py-3 { padding-top: 0.125rem !important; padding-bottom: 0.125rem !important; }
          .mb-2 { margin-bottom: 0.125rem !important; }
          .mb-1 { margin-bottom: 0.0625rem !important; }
          .pb-4 { padding-bottom: 0.25rem !important; }
          
                     /* Reduced spacing */
           .space-y-6 > * + * { margin-top: 0.25rem !important; }
           .space-y-2 > * + * { margin-top: 0.0625rem !important; }
           .gap-6 { gap: 0.25rem !important; }
          
          /* Layout */
          .grid { display: grid !important; }
          .grid-cols-1 { grid-template-columns: repeat(1, minmax(0, 1fr)) !important; }
          .md\\:grid-cols-2 { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; }
          .overflow-x-auto { overflow-x: visible !important; }
          .rounded-lg { border-radius: 0.25rem !important; }
          
          /* Borders */
          .border-b-2 { border-bottom-width: 2px !important; }
          .border-b { border-bottom-width: 1px !important; }
          .border-t-2 { border-top-width: 2px !important; }
          
          /* Width and positioning */
          .w-full { width: 100% !important; }
          .w-32 { width: 6rem !important; }
          .mx-auto { margin-left: auto !important; margin-right: auto !important; }
          
          /* Flexbox */
          .flex { display: flex !important; }
          .justify-between { justify-content: space-between !important; }
          .justify-end { justify-content: flex-end !important; }
          .items-start { align-items: flex-start !important; }
          .items-center { align-items: center !important; }
          .space-x-3 > * + * { margin-left: 0.5rem !important; }
          .space-x-2 > * + * { margin-left: 0.25rem !important; }
          
          /* Icons */
          .h-4 { height: 0.75rem !important; }
          .w-4 { width: 0.75rem !important; }
          
          /* Colors */
          .text-gray-900 { color: #111827 !important; }
          .text-gray-700 { color: #374151 !important; }
          .text-gray-600 { color: #4b5563 !important; }
          .text-gray-50 { background-color: #f9fafb !important; }
          .bg-white { background-color: white !important; }
          .border-gray-200 { border-color: #e5e7eb !important; }
          .border-gray-300 { border-color: #d1d5db !important; }
          .border-gray-400 { border-color: #9ca3af !important; }
          
          /* Hide buttons and other elements */
          .btn { display: none !important; }
          .flex.justify-end.space-x-3 { display: none !important; }
          
          /* Hide any page titles or headers that might contain "Generate Invoice" */
          h1:not(.text-3xl), h2:not(.text-2xl), h3:not(.text-lg) { display: none !important; }
          .page-title, .header-title, .modal-title { display: none !important; }
          
          /* Ensure invoice title is ALWAYS visible in print */
          .invoice-title h2 { 
            display: block !important; 
            color: #111827 !important; 
            font-weight: bold !important; 
            font-size: 1.5rem !important;
            text-align: center !important;
            margin-bottom: 1rem !important;
          }
          

          
          /* Ensure invoice container is visible and properly sized */
          .invoice-container { 
            display: block !important; 
            width: 100% !important;
            max-width: none !important;
            margin: 0 !important;
            padding: 0.5rem !important;
          }
          
          /* Force single page */
          .invoice-container { page-break-after: avoid !important; page-break-before: avoid !important; }
          
          /* Ensure company header is visible */
          .text-center.border-b-2 { 
            border-bottom: 2px solid #d1d5db !important; 
            padding-bottom: 0.5rem !important; 
            margin-bottom: 0.5rem !important; 
          }
          
          /* Ensure all headings are visible */
          h1, h2, h3, h4 { 
            color: #111827 !important; 
            font-weight: bold !important; 
            display: block !important; 
          }
        }
      `}</style>
      
      {/* Invoice Title - At the very top */}
      <div className="text-center mb-4 invoice-title">
        <h2 className="text-3xl font-bold text-gray-900">
          {workEntry.taxAmount && parseFloat(workEntry.taxAmount) > 0 ? 'TAX INVOICE' : 'INVOICE'}
        </h2>
      </div>

      {/* Company Header */}
      <div className="text-center border-b-2 border-gray-300 pb-4">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">SRI VELMURUGAN TRANSPORT AND EARTHMOVERS</h1>
        <p className="text-gray-700 mb-1">D.No. 220, Shop No.01, Tamil Nagar, Punjai Kalamangalam, Ganapathipalayam, Erode, Tamil Nadu-638153.</p>
        <p className="text-gray-700 mb-1">Mobile: (+91)9788303040</p>
        {workEntry.taxAmount && parseFloat(workEntry.taxAmount) > 0 && (
          <p className="text-gray-700 mb-1">GST: 33FUJPB2105R1ZL</p>
        )}
      </div>

             {/* Invoice Details */}
       <div className="flex justify-between items-start">
         <div>
           <p className="text-gray-600">Invoice No: {workEntry.invoiceNumber || `2025-26/${workEntry.id.slice(0, 2).toUpperCase()}`}</p>
           <p className="text-gray-600">Date: {formatDate(workEntry.workDate)}</p>
         </div>
       </div>

      {/* Billing and Shipping Information */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-gray-50 p-4 rounded-lg">
          <h4 className="font-semibold text-gray-900 mb-2">BILL TO:</h4>
          <p className="font-medium">{customer?.company || customer?.name}</p>
          <p className="text-gray-600">{customer?.address || 'Address not provided'}</p>
          {customer?.gstin && customer.gstin.trim() !== '' && (
            <p className="text-gray-600">GST: {customer.gstin}</p>
          )}
        </div>
        <div className="bg-gray-50 p-4 rounded-lg">
          <h4 className="font-semibold text-gray-900 mb-2">SHIP TO:</h4>
          <p className="font-medium">{customer?.company || customer?.name}</p>
          <p className="text-gray-600">{customer?.address || 'Address not provided'}</p>
          
        </div>
      </div>



      {/* Invoice Items */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
          <h4 className="font-semibold text-gray-900">Invoice Items</h4>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                                 <th className="px-4 py-3 text-center text-sm font-medium text-gray-900 border border-gray-300 w-16">S No</th>
                 <th className="px-4 py-3 text-center text-sm font-medium text-gray-900 border border-gray-300 w-48">Description</th>
                 <th className="px-4 py-3 text-center text-sm font-medium text-gray-900 border border-gray-300 w-20">HSN</th>
                 <th className="px-4 py-3 text-center text-sm font-medium text-gray-900 border border-gray-300 w-24">Qty (Hours)</th>
                 <th className="px-4 py-3 text-center text-sm font-medium text-gray-900 border border-gray-300 w-24">Rate</th>
                 <th className="px-4 py-3 text-center text-sm font-medium text-gray-900 border border-gray-300 w-24">Amount</th>
                 {discountAmount > 0 && (
                   <th className="px-4 py-3 text-center text-sm font-medium text-gray-900 border border-gray-300 w-28">After Discount</th>
                 )}
                 {workEntry.taxAmount && parseFloat(workEntry.taxAmount) > 0 && (
                   <>
                     <th className="px-4 py-3 text-center text-sm font-medium text-gray-900 border border-gray-300 w-16">GST</th>
                     <th className="px-4 py-3 text-center text-sm font-medium text-gray-900 border border-gray-300 w-24">Tax</th>
                   </>
                 )}
                 <th className="px-4 py-3 text-center text-sm font-medium text-gray-900 border border-gray-300 w-28">Total</th>
              </tr>
            </thead>
            <tbody>
                             {workEntry.workItems && workEntry.workItems.length > 0 ? (
                 // Display multiple work items
                 workEntry.workItems.map((item, index) => (
                   <tr key={index} className="border-b border-gray-200">
                     <td className="px-4 py-3 text-sm text-gray-900 border border-gray-300 text-center">
                       {item.serialNumber || (index + 1)}
                     </td>
                     <td className="px-4 py-3 text-sm text-gray-900 border border-gray-300 break-words whitespace-normal text-center">
                       {item.description}
                     </td>
                     <td className="px-4 py-3 text-sm text-gray-900 border border-gray-300 text-center">
                       {item.hsnCode || '997313'}
                     </td>
                     <td className="px-4 py-3 text-sm text-gray-900 border border-gray-300 text-center">
                       {item.hoursWorked}
                     </td>
                     <td className="px-4 py-3 text-sm text-gray-900 border border-gray-300 text-center">
                       {parseFloat(item.ratePerHour || 0).toLocaleString()}
                     </td>
                     <td className="px-4 py-3 text-sm text-gray-900 border border-gray-300 text-center">
                       {(parseFloat(item.amount || 0) + parseFloat(item.betaAmount || 0)).toLocaleString()}
                     </td>
                                            {discountAmount > 0 && index === 0 && (
                         <td rowSpan={workEntry.workItems.length} className="px-4 py-3 text-sm text-gray-900 border border-gray-300 text-center align-middle">
                           {Math.round(baseAmountAfterDiscount).toLocaleString()} (-{Math.round(discountAmount).toLocaleString()})
                         </td>
                       )}
                     {workEntry.taxAmount && parseFloat(workEntry.taxAmount) > 0 && index === 0 && (
                       <>
                         <td rowSpan={workEntry.workItems.length} className="px-4 py-3 text-sm text-gray-900 border border-gray-300 text-center align-middle">
                           18%
                         </td>
                         <td rowSpan={workEntry.workItems.length} className="px-4 py-3 text-sm text-gray-900 border border-gray-300 text-center align-middle">
                           {Math.round(parseFloat(workEntry.taxAmount || 0)).toLocaleString()}
                         </td>
                       </>
                     )}
                     {index === 0 && (
                       <td rowSpan={workEntry.workItems.length} className="px-4 py-3 text-sm text-gray-900 border border-gray-300 text-center align-middle font-semibold">
                         {Math.round(totalAmount).toLocaleString()}
                       </td>
                     )}
                   </tr>
                 ))
              ) : (
                // Fallback to single work item (backward compatibility)
                <tr className="border-b border-gray-200">
                  <td className="px-4 py-3 text-sm text-gray-900 border border-gray-300 text-center">
                    {workEntry.serialNumber ? (
                      <div className="space-y-1">
                        {workEntry.serialNumber.split(/[,\n]/).map((sn, index) => (
                          <div key={index} className="text-xs bg-gray-100 px-1 py-0.5 rounded">
                            {sn.trim()}
                          </div>
                        ))}
                      </div>
                    ) : (
                      '1'
                    )}
                  </td>
                                     <td className="px-4 py-3 text-sm text-gray-900 border border-gray-300 break-words whitespace-normal text-center">
                     {workEntry.workDescription}
                   </td>
                  <td className="px-4 py-3 text-sm text-gray-900 border border-gray-300 text-center">
                    {workEntry.hsnCode || '997313'}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900 border border-gray-300 text-center">
                    {workEntry.hoursWorked}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900 border border-gray-300 text-center">
                    {parseFloat(workEntry.ratePerHour || 0).toLocaleString()}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900 border border-gray-300 text-center">
                    {parseFloat(workEntry.amount || 0).toLocaleString()}
                  </td>
                                     {discountAmount > 0 && (
                     <td className="px-4 py-3 text-sm text-gray-900 border border-gray-300 text-center">
                       {Math.round(baseAmountAfterDiscount).toLocaleString()} (-{Math.round(discountAmount).toLocaleString()})
                     </td>
                   )}
                  {workEntry.taxAmount && parseFloat(workEntry.taxAmount) > 0 && (
                    <>
                      <td className="px-4 py-3 text-sm text-gray-900 border border-gray-300 text-center">
                        18%
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900 border border-gray-300 text-center">
                        {Math.round(parseFloat(workEntry.taxAmount || 0)).toLocaleString()}
                      </td>
                    </>
                  )}
                  <td className="px-4 py-3 text-sm text-gray-900 border border-gray-300 text-center font-semibold">
                    {Math.round(parseFloat(totalAmount)).toLocaleString()}
                  </td>
                </tr>
              )}

              
              

              
              {/* Final Total Row */}
              <tr className="bg-blue-50 border-t-2 border-blue-300">
                <td colSpan={(() => {
                  let colSpan = 6 // Base columns: S No, Description, HSN, Qty (Hours), Rate, Amount
                  if (discountAmount > 0) colSpan += 1 // Add after discount column only
                  if (workEntry.taxAmount && parseFloat(workEntry.taxAmount) > 0) colSpan += 2 // Add GST and Tax columns
                  return colSpan
                })()} className="px-4 py-4 text-lg font-bold text-blue-900 border border-gray-300 text-right">
                  TOTAL AMOUNT:
                </td>
                <td className="px-4 py-4 text-lg font-bold text-blue-900 border border-gray-300 text-right">
                  ₹{Math.round(totalAmount).toLocaleString()}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Tax Details - Only show if GST is enabled */}
      {workEntry.taxAmount && parseFloat(workEntry.taxAmount) > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden mt-6">
          <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
            <h4 className="font-semibold text-gray-900">Tax Details</h4>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full table-fixed min-w-[800px]">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-center text-sm font-medium text-gray-900 border border-gray-300">Tax Rate(%)</th>
                  <th className="px-4 py-3 text-center text-sm font-medium text-gray-900 border border-gray-300">Taxable amount</th>
                  <th className="px-4 py-3 text-center text-sm font-medium text-gray-900 border border-gray-300">CGST Amount (9%)</th>
                  <th className="px-4 py-3 text-center text-sm font-medium text-gray-900 border border-gray-300">SGST Amount (9%)</th>
                  <th className="px-4 py-3 text-center text-sm font-medium text-gray-900 border border-gray-300">Total Tax</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-gray-200">
                  <td className="px-4 py-3 text-sm text-gray-900 border border-gray-300 text-center">18%</td>
                  <td className="px-4 py-3 text-sm text-gray-900 border border-gray-300 text-center">
                    {baseAmountAfterDiscount.toLocaleString()}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900 border border-gray-300 text-center">
                    {(parseFloat(workEntry.taxAmount || 0) / 2).toLocaleString()}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900 border border-gray-300 text-center">
                    {(parseFloat(workEntry.taxAmount || 0) / 2).toLocaleString()}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900 border border-gray-300 text-center">
                    {parseFloat(workEntry.taxAmount || 0).toLocaleString()}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      

      {/* Account Details and Signature */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-gray-50 p-4 rounded-lg">
          <h4 className="font-semibold text-gray-900 mb-2">Account Details:</h4>
          <p className="text-gray-700 mb-1"><strong>Ac No:</strong> 510909010359515</p>
          <p className="text-gray-700 mb-1"><strong>IFSC Code:</strong> CIUB0000542</p>
          <p className="text-gray-700 mb-1"><strong>Bank Name:</strong> City Union Bank</p>
          <p className="text-gray-700"><strong>Branch:</strong> Modakurichi</p>
        </div>
                 <div className="bg-gray-50 p-4 rounded-lg flex flex-col justify-between">
           <div></div>
           <div className="text-center">
                                          <img 
                 src="/images/signature.png" 
                 alt="Authorized Signature" 
                 className="w-48 h-24 mx-auto mb-1 object-contain"
                 style={{ maxHeight: '100px' }}
                 onError={(e) => {
                   e.target.style.display = 'none';
                   e.target.nextSibling.style.display = 'block';
                 }}
               />
             <div className="border-t-2 border-gray-400 w-32 mx-auto mb-2" style={{ display: 'none' }}></div>
             <p className="text-gray-700 font-semibold">For Sri Velmurugan Transport and Earthmovers</p>
             <p className="text-gray-600 text-sm">Authorized Signatory</p>
           </div>
         </div>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-end space-x-3">
        <button
          onClick={handleDownload}
          className="btn btn-success flex items-center space-x-2"
          style={{ backgroundColor: '#DC2626', borderColor: '#DC2626', color: 'white' }}
        >
          <Download className="h-4 w-4" />
          <span>Download PDF</span>
        </button>
      </div>
    </div>
  )
}

export default InvoiceGenerator
